import React, { useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, AlertCircle, BookOpen, ShieldCheck, Loader2 } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import FlagStats from './FlagStats';
import FlagFilters from './FlagFilters';
import FlagCard from './FlagCard';
import { spendTier } from './spendTiers';
import './FlagReview.css';

const QUEUE_LIMIT = 100;

/**
 * FlagReview — operator-gated review of items flagged from drilldowns.
 *
 * The North Star (Principle 3) makes PoClassifier the only canonical writer.
 * Phase 1 (this page) covers the three resolution actions that do NOT write
 * classification_method:
 *   • Confirm correct — current classification stands as-is
 *   • Promote to eval set — row becomes a gold-standard prior
 *   • Dismiss (duplicate / not actionable)
 *
 * The two write-actions — "Reclassify with AI" and "Manually classify" —
 * are rendered as locked Phase-2 affordances. They route through a
 * backend wrapper that calls PoClassifier with a wave-id; building that
 * wrapper is Phase 2.
 */
function FlagReview({ department, departmentId }) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    category: 'all',
    tier: 'all',
    sort: 'priority',
  });
  // Local map of item_id → proposal (from /reclassify endpoint). Lives in
  // memory only; refreshing the page clears all in-flight proposals.
  const [proposals, setProposals] = useState({});

  const queueParams = useMemo(() => {
    const p = { limit: QUEUE_LIMIT };
    if (department) p.department = department;
    return p;
  }, [department]);

  const queueQuery = useQuery({
    queryKey: ['classification-flags', 'queue', queueParams],
    queryFn: () => apiClient.get('/api/classification/flags/queue', { params: queueParams }),
    staleTime: 30_000,
  });

  const statsQuery = useQuery({
    queryKey: ['classification-flags', 'stats', department || null],
    queryFn: () => apiClient.get('/api/classification/flags/stats', {
      params: department ? { department } : {},
    }),
    staleTime: 60_000,
  });

  // Optimistic queue removal (UI-responsiveness review 2026-08-08): resolving
  // or applying drops the card the instant the operator acts — this is the
  // triage loop, and waiting a round-trip + full queue refetch per decision
  // adds up. onMutate snapshots the queue cache and rolls back on error; the
  // invalidate on settle reconciles stats and the tail of the queue.
  const queueKey = ['classification-flags', 'queue', queueParams];
  const dropFromQueue = async (itemId) => {
    await queryClient.cancelQueries({ queryKey: queueKey });
    const previous = queryClient.getQueryData(queueKey);
    queryClient.setQueryData(queueKey, (prev) => (
      prev && Array.isArray(prev.queue)
        ? {
            ...prev,
            queue: prev.queue.filter((r) => r.item_id !== itemId),
            total_items: Math.max(0, (prev.total_items || 1) - 1),
          }
        : prev
    ));
    return { previous };
  };
  const restoreQueue = (ctx) => {
    if (ctx?.previous) queryClient.setQueryData(queueKey, ctx.previous);
  };

  const resolveMutation = useMutation({
    mutationFn: ({ itemId, body }) =>
      apiClient.post(`/api/classification/flags/item/${itemId}/resolve`, body),
    onMutate: ({ itemId }) => dropFromQueue(itemId),
    onSuccess: (_data, { itemId, body }) => {
      logger.ui.info('FlagReview', 'resolved', {
        item_id: itemId,
        resolution_type: body.resolution_type,
        status: body.status,
      });
    },
    onError: (err, { itemId }, ctx) => {
      restoreQueue(ctx);
      logger.ui.error('FlagReview', 'resolve_failed', {
        item_id: itemId, message: err?.message,
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['classification-flags'] }),
  });

  // Reclassify (writes shadow only). Result drops into `proposals[itemId]`
  // and the card renders the diff inline. Pass `escalate=true` to attach
  // PoHaikuShadow so the cascade can hit Haiku for items ≥$10K where the
  // deterministic methods produced nothing.
  const reclassifyMutation = useMutation({
    mutationFn: ({ itemId, escalate, useSonnet, hintText }) =>
      apiClient.post(`/api/classification/flags/item/${itemId}/reclassify`, {
        escalate: !!escalate,
        use_sonnet: !!useSonnet,
        ...(hintText && hintText.trim().length > 0 ? { hint_text: hintText.trim() } : {}),
      }),
    onSuccess: (data, { itemId, escalate, useSonnet, hintText }) => {
      logger.ui.info('FlagReview', 'reclassify_ok', {
        item_id: itemId,
        wave_id: data?.wave_id,
        changed: data?.changed,
        trust: data?.trust,
        escalated: !!escalate,
        used_sonnet: !!useSonnet,
        hint_supplied: !!(hintText && hintText.trim().length > 0),
        escalation_cost_usd: data?.escalation?.cost_usd,
        sonnet_cost_usd: data?.sonnet_escalation?.cost_usd,
        sonnet_tool_calls: data?.sonnet_escalation?.tool_calls,
        sonnet_web_searches: data?.sonnet_escalation?.web_searches,
      });
      // Refresh the queue so the flag's hint panel reflects the newly-saved
      // hint_text (the backend persists body.hint_text onto the latest flag).
      queryClient.invalidateQueries({ queryKey: ['classification-flags'] });
      setProposals(p => ({ ...p, [itemId]: data }));
    },
    onError: (err, { itemId }) => {
      logger.ui.error('FlagReview', 'reclassify_failed', {
        item_id: itemId, message: err?.message,
      });
      // Surface the failure inline so the operator isn't left wondering
      // why nothing happened. Store as a pseudo-proposal — the card
      // renders an error banner when proposal.errorMessage is set.
      setProposals(p => ({
        ...p,
        [itemId]: {
          errorMessage: err?.message || 'Reclassify failed — check the API response.',
          errorStatus: err?.status || err?.response?.status,
        },
      }));
    },
  });

  // Apply = promote shadow → primary + resolve flag with
  // resolution_type='reclassified'. Backend handles both in one call. The
  // card leaves the queue on click (the outcome — item resolved — is known;
  // only the write is pending); a failure restores it.
  const applyMutation = useMutation({
    mutationFn: ({ itemId }) =>
      apiClient.post(`/api/classification/flags/item/${itemId}/apply-proposal`, {}),
    onMutate: ({ itemId }) => dropFromQueue(itemId),
    onSuccess: (data, { itemId }) => {
      logger.ui.info('FlagReview', 'apply_ok', {
        item_id: itemId,
        wave_id: data?.wave_id,
        flags_closed: data?.flags_closed,
      });
      setProposals(p => {
        const next = { ...p };
        delete next[itemId];
        return next;
      });
    },
    onError: (err, { itemId }, ctx) => {
      restoreQueue(ctx);
      logger.ui.error('FlagReview', 'apply_failed', {
        item_id: itemId, message: err?.message,
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['classification-flags'] }),
  });

  const handleResolve = useCallback((itemId, body) => {
    resolveMutation.mutate({ itemId, body });
  }, [resolveMutation]);

  const handleReclassify = useCallback((itemId, opts = {}) => {
    reclassifyMutation.mutate({
      itemId,
      escalate: !!opts.escalate,
      useSonnet: !!opts.useSonnet,
      hintText: opts.hintText || '',
    });
  }, [reclassifyMutation]);

  const handleApply = useCallback((itemId) => {
    applyMutation.mutate({ itemId });
  }, [applyMutation]);

  const handleClearProposal = useCallback((itemId) => {
    setProposals(p => {
      const next = { ...p };
      delete next[itemId];
      return next;
    });
  }, []);

  // Apply client-side filters/sort on the queue payload. The backend
  // already filters by department; tier + category + sort are local
  // because the backend doesn't (yet) expose these knobs and the
  // priority_score is already computed server-side.
  const filteredQueue = useMemo(() => {
    let rows = queueQuery.data?.queue || [];
    if (filters.category !== 'all') {
      rows = rows.filter(r => (r.categories || []).includes(filters.category));
    }
    if (filters.tier !== 'all') {
      rows = rows.filter(r => spendTier(r.line_total).key === filters.tier);
    }
    const sorted = [...rows];
    switch (filters.sort) {
      case 'amount_desc':
        sorted.sort((a, b) => (Number(b.line_total) || 0) - (Number(a.line_total) || 0));
        break;
      case 'age_desc':
        sorted.sort((a, b) => new Date(a.first_flagged_at) - new Date(b.first_flagged_at));
        break;
      case 'flag_count_desc':
        sorted.sort((a, b) => (b.flag_count || 0) - (a.flag_count || 0));
        break;
      case 'priority':
      default:
        sorted.sort((a, b) => (Number(b.priority_score) || 0) - (Number(a.priority_score) || 0));
    }
    return sorted;
  }, [queueQuery.data, filters]);

  const handleRefresh = useCallback(() => {
    queueQuery.refetch();
    statsQuery.refetch();
  }, [queueQuery, statsQuery]);

  return (
    <div className="fr-page">
      <header className="fr-page__head">
        <div className="fr-page__title">
          <Flag size={22} />
          <div>
            <h1>Classification flag review</h1>
            <p className="fr-page__sub">
              Operator-gated queue for items flagged from drilldown modals.
              Confirmations and dismissals close out without touching
              <code> classification_method</code>; AI-driven and manual
              reclassification routes through PoClassifier in Phase 2 —
              the only canonical writer (see CLASSIFICATION_NORTH_STAR.md).
            </p>
          </div>
        </div>
        <div className="fr-page__principles" title="Guiding principles from CLASSIFICATION_NORTH_STAR.md">
          <span className="fr-pill"><BookOpen size={11} /> Principle 3 · single writer</span>
          <span className="fr-pill"><ShieldCheck size={11} /> Principle 5 · fix the classifier, not the data</span>
        </div>
      </header>

      <FlagStats
        stats={statsQuery.data}
        isLoading={statsQuery.isLoading}
        error={statsQuery.error}
        sort={filters.sort}
        onDrillTo={(action) => {
          if (action.kind === 'reset') {
            setFilters({ category: 'all', tier: 'all', sort: 'priority' });
          } else if (action.kind === 'sort') {
            setFilters(f => ({ ...f, sort: action.value }));
          }
        }}
      />

      <FlagFilters
        value={filters}
        onChange={setFilters}
        onRefresh={handleRefresh}
        isLoading={queueQuery.isFetching}
      />

      <main className="fr-list">
        {queueQuery.isLoading && (
          <div className="fr-state fr-state--loading">
            <Loader2 size={18} className="fr-spin" /> Loading flag queue…
          </div>
        )}
        {queueQuery.error && (
          <div className="fr-state fr-state--error">
            <AlertCircle size={18} /> Failed to load queue: {queueQuery.error.message}
          </div>
        )}
        {!queueQuery.isLoading && !queueQuery.error && filteredQueue.length === 0 && (
          <div className="fr-state fr-state--empty">
            <ShieldCheck size={28} />
            <h3>Nothing to review</h3>
            <p>No open flags match the current filters.</p>
          </div>
        )}
        {filteredQueue.map(flag => (
          <FlagCard
            key={flag.item_id}
            flag={flag}
            proposal={proposals[flag.item_id]}
            onResolve={handleResolve}
            onReclassify={handleReclassify}
            onApply={handleApply}
            onClearProposal={handleClearProposal}
            busy={resolveMutation.isPending && resolveMutation.variables?.itemId === flag.item_id}
            reclassifying={reclassifyMutation.isPending && reclassifyMutation.variables?.itemId === flag.item_id}
            applying={applyMutation.isPending && applyMutation.variables?.itemId === flag.item_id}
          />
        ))}
      </main>

      <footer className="fr-page__foot">
        <span>
          Showing {filteredQueue.length} of {queueQuery.data?.queue?.length || 0} open flags
          {queueQuery.data?.total_items
            ? ` · ${queueQuery.data.total_items} total items in queue`
            : ''}
          {department ? ` · scoped to ${department}` : ''}
        </span>
      </footer>
    </div>
  );
}

export default FlagReview;
